/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// @mui material components
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import Avatar from '@mui/material/Avatar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import * as Func from 'services/Function';
import { APP_STATE } from 'services/Define';
import ProductImage from './ProductImage';
import ProductBranch from './ProductBranch';
import ProductSupplier from './ProductSupplier';
import ProductPromotion from './ProductPromotion';
import ProductCost from './ProductCost';
import ProductSeller from './ProductSeller';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme }) => ({
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      style: {
        transform: 'rotate(0deg)',
      },
    },
    {
      props: ({ expand }) => !!expand,
      style: {
        transform: 'rotate(180deg)',
      },
    },
  ],
}));

function ProductPackaging({ row, open, pin, del, pack, print, reload }) {
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const preReload = () => {
    reload();
  };

  const btnOpen = (item, pack = 'N') => {
    const stylePack = {
      fontSize: '1rem',
      whiteSpace: 'nowrap',
      textAlign: 'left',
      display: 'block',
      padding: '0',
      fontWeight: 500,
    };

    const styleSKU = {
      fontSize: '1rem',
      whiteSpace: 'nowrap',
      textAlign: 'left',
      display: 'block',
      padding: '0',
    };

    return (
      <Tooltip title='แก้ไขข้อมูล'>
        <MDButton
          variant='text'
          color={item.product_main === 'Y' ? 'info' : 'dark'}
          size='small'
          sx={pack === 'Y' ? stylePack : styleSKU}
          onClick={() => open(item)}
        >
          {item.barcode}
        </MDButton>
      </Tooltip>
    );
  };

  const btnPackaging = (item) => {
    return (
      <Tooltip title='เพิ่มบรรจุภัณฑ์'>
        <IconButton aria-label='Packaging' color='warning' onClick={() => pack(item)}>
          <Icon>inventory_2</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnProductMain = (item) => {
    if (item.product_main !== 'Y') return null;
    return (
      <Tooltip title='สินค้าหลัก'>
        <IconButton aria-label='settings' color='info'>
          <Icon>push_pin</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    if (item.product_main === 'Y') return null;
    if (!app.perm.del) return null;

    return (
      <Tooltip title={t(`element.delete`)}>
        <IconButton aria-label='delete' color='error' onClick={() => del(item)}>
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const btnBarcode = (item) => {
    if (item.barcode === '') return null;

    return (
      <Tooltip title='Barcode'>
        <MDButton
          variant='text'
          color='secondary'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'inline-block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
          onClick={() => print(item.barcode)}
        >
          <Icon>qr_code</Icon>
          {item.barcode}
        </MDButton>
      </Tooltip>
    );
  };

  const btnBarcode2 = (item) => {
    if (item.barcode2 === '') return '-';

    return (
      <Tooltip title='Barcode 2'>
        <MDButton
          variant='text'
          color='secondary'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'inline-block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
          onClick={() => print(item.barcode2)}
        >
          <Icon>qr_code_2</Icon>
          {item.barcode2}
        </MDButton>
      </Tooltip>
    );
  };

  const minStockDisplay = (item) => {
    if (item.check_stock === 'Y') {
      return (
        <MDBox>
          {Func.numberDisplay(item.min_stock)} {row.unit_name}
        </MDBox>
      );
    }
    return '-';
  };

  const maxStockDisplay = (item) => {
    if (item.check_stock === 'Y') {
      return (
        <MDBox>
          {Func.numberDisplay(item.max_stock)} {row.unit_name}
        </MDBox>
      );
    }
    return '-';
  };

  const sizeDisplay = (item) => {
    if (item.color_name === '') {
      if (item.size_name === '')
        return (
          <Avatar aria-label='size-color'>
            <Icon>texture</Icon>
          </Avatar>
        );

      return <Avatar aria-label='size-color'>{item.size_name}</Avatar>;
    }

    if (item.size_name === '')
      return (
        <Avatar sx={{ backgroundColor: item.color }} variant='outlined' aria-label='size-color'>
          <Icon>format_paint</Icon>
        </Avatar>
      );

    return (
      <Avatar sx={{ backgroundColor: item.color }} variant='outlined' aria-label='size-color'>
        {item.size_name}
      </Avatar>
    );
  };

  const renderList = (packaging) => {
    return (
      <MDBox>
        <Grid container>
          {packaging.map((item) => (
            <Grid item xs={12} key={item.id}>
              <MDBox sx={{ borderTop: '1px solid #e2e2e2' }}>
                <CardHeader
                  avatar={<ProductImage parent={item} callback={pin} />}
                  title={btnOpen(item, 'Y')}
                  subheader={item.name}
                  action={
                    <Tooltip title='บรรจุภัณฑ์'>
                      <Icon color='secondary'>inventory_2</Icon>
                    </Tooltip>
                  }
                />
                <CardContent sx={{ px: 2, py: 0, fontSize: 15, color: '#666666', fontWeight: 100 }}>
                  <Grid container>
                    <Grid item xs={6}>
                      Barcode
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      {btnBarcode(item)}
                    </Grid>
                    <Grid item xs={6}>
                      Barcode 2
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      {btnBarcode2(item)}
                    </Grid>
                    <Grid item xs={6}>
                      หน่วยนับ
                    </Grid>
                    <Grid item xs={6} className='text-right color-info'>
                      {item.unit_name}
                    </Grid>
                    <Grid item xs={6}>
                      อัตราส่วน
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      {Func.numberDisplay(item.ratio)} {item.unit_main}
                    </Grid>
                    <Grid item xs={6}>
                      ราคาทุน
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      <ProductCost parent={item} />
                    </Grid>
                    <Grid item xs={6}>
                      ราคาซื้อ
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      <ProductSupplier parent={item} reload={preReload} />
                    </Grid>
                    <Grid item xs={6}>
                      ราคาขาย
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      <ProductBranch parent={item} reload={preReload} />
                    </Grid>
                    <Grid item xs={6}>
                      ค่าหยิบสินค้า
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      {Func.numberDisplay(item.affiliate, 2)} ฿
                    </Grid>
                    <Grid item xs={6}>
                      เปิดขายหน้าร้าน
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      <Switch size='small' color='info' checked={item.enable === 'Y'} />
                    </Grid>
                    <Grid item xs={6}>
                      เปิดขาย ONLINE
                    </Grid>
                    <Grid item xs={6} className='text-right'>
                      <Switch size='small' color='success' checked={item.sell_online === 'Y'} />
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions disableSpacing>{btnDel(item)}</CardActions>
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    );
  };

  return (
    <MDBox className='card'>
      <CardHeader
        avatar={sizeDisplay(row)}
        action={btnProductMain(row)}
        title={btnOpen(row)}
        subheader={row.name}
      />
      <ProductImage parent={row} callback={pin} size='large' />
      <CardContent sx={{ p: 2, pb: 0, fontSize: '1rem', color: '#666666', fontWeight: 100 }}>
        <Grid container>
          <Grid item xs={6}>
            Barcode
          </Grid>
          <Grid item xs={6} className='text-right'>
            {btnBarcode(row)}
          </Grid>
          <Grid item xs={6}>
            Rating
          </Grid>
          <Grid item xs={6} className='text-right'>
            {row.best_name}
          </Grid>
          <Grid item xs={6}>
            Barcode 2
          </Grid>
          <Grid item xs={6} className='text-right'>
            {btnBarcode2(row)}
          </Grid>
          <Grid item xs={6}>
            สี
          </Grid>
          <Grid item xs={6} className='text-right'>
            {row.color_name === '' ? '-' : row.color_name}
          </Grid>
          <Grid item xs={6}>
            ขนาด
          </Grid>
          <Grid item xs={6} className='text-right'>
            {row.size_name === '' ? '-' : row.size_name}
          </Grid>
          <Grid item xs={6}>
            หน่วยนับ
          </Grid>
          <Grid item xs={6} className='text-right color-info'>
            {row.unit_name}
          </Grid>
          <Grid item xs={6}>
            ราคาทุน
          </Grid>
          <Grid item xs={6} className='text-right'>
            <ProductCost parent={row} />
          </Grid>
          <Grid item xs={6}>
            ราคาซื้อ
          </Grid>
          <Grid item xs={6} className='text-right'>
            <ProductSupplier parent={row} reload={preReload} />
          </Grid>
          <Grid item xs={6}>
            ราคาขาย
          </Grid>
          <Grid item xs={6} className='text-right'>
            <ProductBranch parent={row} reload={preReload} />
          </Grid>
          <Grid item xs={6}>
            ค่าหยิบสินค้า
          </Grid>
          <Grid item xs={6} className='text-right'>
            {Func.numberDisplay(row.affiliate, 2)} ฿
          </Grid>
          <Grid item xs={6}>
            MIN/MAX
          </Grid>
          <Grid item xs={6} className='text-right'>
            <ProductSeller parent={row} reload={preReload} />
          </Grid>
          <Grid item xs={6}>
            เปิดขายหน้าร้าน
          </Grid>
          <Grid item xs={6} className='text-right'>
            <Switch size='small' color='info' checked={row.enable === 'Y'} />
          </Grid>
          <Grid item xs={6}>
            เปิดขาย ONLINE
          </Grid>
          <Grid item xs={6} className='text-right'>
            <Switch size='small' color='success' checked={row.sell_online === 'Y'} />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions disableSpacing>
        <ProductPromotion parent={row} reload={preReload} />
        {btnPackaging(row)}
        {btnDel(row)}

        <ExpandMore
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label='show more'
        >
          <Tooltip title='แสดงบรรจุภัณฑ์'>
            <ExpandMoreIcon />
          </Tooltip>
        </ExpandMore>
      </CardActions>
      <Collapse in={expanded} timeout='auto' unmountOnExit>
        {renderList(row.packaging)}
      </Collapse>
    </MDBox>
  );
}

ProductPackaging.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string.isRequired,
    no: PropTypes.number.isRequired,
    barcode: PropTypes.string.isRequired,
    barcode2: PropTypes.string.isRequired,
    size_name: PropTypes.string.isRequired,
    color_name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    unit_name: PropTypes.string.isRequired,
    product_main: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    ratio: PropTypes.string.isRequired,
    unit_main: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    enable: PropTypes.string.isRequired,
    sell_online: PropTypes.string.isRequired,
    price_auto: PropTypes.string.isRequired,
    min_stock: PropTypes.string.isRequired,
    max_stock: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    affiliate: PropTypes.string.isRequired,
    best_name: PropTypes.string.isRequired,
    packaging: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        barcode: PropTypes.string.isRequired,
        barcode2: PropTypes.string.isRequired,
        size_name: PropTypes.string.isRequired,
        color_name: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        unit_name: PropTypes.string.isRequired,
        product_main: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        ratio: PropTypes.string.isRequired,
        unit_main: PropTypes.string.isRequired,
        price: PropTypes.string.isRequired,
        enable: PropTypes.string.isRequired,
        sell_online: PropTypes.string.isRequired,
        price_auto: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }).isRequired,
  open: PropTypes.func.isRequired,
  pin: PropTypes.func.isRequired,
  pack: PropTypes.func.isRequired,
  del: PropTypes.func.isRequired,
  reload: PropTypes.func.isRequired,
  print: PropTypes.func.isRequired,
};

export default ProductPackaging;
